import { UserRole, AppError } from './types';
import { APP_CONFIG, HTTP_STATUS, ERROR_CODES } from './constants';

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

export function isValidFileType(filename: string): boolean {
  const extension = filename.toLowerCase().substring(filename.lastIndexOf('.')) as '.mp4' | '.mkv';
  return APP_CONFIG.ALLOWED_FILE_TYPES.includes(extension);
}

export function isValidMimeType(mimeType: string): boolean {
  return APP_CONFIG.ALLOWED_MIME_TYPES.includes(mimeType as 'video/mp4' | 'video/x-matroska');
}

export function validateFileUpload(filename: string, fileSize: number, mimeType?: string): void {
  if (!isValidFileType(filename)) {
    throw new AppError(
      `Invalid file type. Allowed types: ${APP_CONFIG.ALLOWED_FILE_TYPES.join(', ')}`,
      HTTP_STATUS.BAD_REQUEST,
      ERROR_CODES.INVALID_FILE_TYPE
    );
  }
  
  if (mimeType && !isValidMimeType(mimeType)) {
    throw new AppError(
      `Invalid MIME type. Allowed types: ${APP_CONFIG.ALLOWED_MIME_TYPES.join(', ')}`,
      HTTP_STATUS.BAD_REQUEST,
      ERROR_CODES.INVALID_FILE_TYPE
    );
  }
  
  if (fileSize > APP_CONFIG.MAX_FILE_SIZE) {
    throw new AppError(
      `File too large. Maximum size: ${formatBytes(APP_CONFIG.MAX_FILE_SIZE)}`,
      HTTP_STATUS.PAYLOAD_TOO_LARGE,
      ERROR_CODES.FILE_TOO_LARGE
    );
  }
}

export function getRetentionPeriod(role: UserRole): number {
  return APP_CONFIG.RETENTION_PERIODS[role];
}

export function isExpired(dateString: string, retentionDays: number): boolean {
  const date = new Date(dateString);
  const expiryDate = new Date(date.getTime() + (retentionDays * 24 * 60 * 60 * 1000));
  return new Date() > expiryDate;
}

export function isLongTerm(dateString: string): boolean {
  const date = new Date(dateString);
  const thresholdDate = new Date(date.getTime() + (APP_CONFIG.LONG_TERM_THRESHOLD_DAYS * 24 * 60 * 60 * 1000));
  return new Date() > thresholdDate;
}

export function generateId(): string {
  return crypto.randomUUID();
}

export function sanitizeFilename(filename: string): string {
  return filename
    .replace(/[^a-zA-Z0-9.-]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_|_$/g, '');
}

export function buildR2Key(template: string, params: Record<string, string>): string {
  let key = template;
  for (const [param, value] of Object.entries(params)) {
    key = key.replace(`{${param}}`, value);
  }
  return key;
}

export function buildKVKey(template: string, params: Record<string, string>): string {
  let key = template;
  for (const [param, value] of Object.entries(params)) {
    key = key.replace(`{${param}}`, value);
  }
  return key;
}

export function parseJWT(token: string): any {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload);
    return JSON.parse(decoded);
  } catch {
    throw new AppError('Invalid JWT token', HTTP_STATUS.UNAUTHORIZED, ERROR_CODES.INVALID_TOKEN);
  }
}

export function isJWTExpired(token: string): boolean {
  try {
    const payload = parseJWT(token);
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

export function createApiResponse<T>(data: T, message?: string) {
  return {
    success: true,
    data,
    message,
  };
}

export function createErrorResponse(error: string | AppError) {
  if (error instanceof AppError) {
    return {
      success: false,
      error: error.message,
      code: error.code,
    };
  }
  
  return {
    success: false,
    error: typeof error === 'string' ? error : 'An unexpected error occurred',
  };
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function chunk<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}